import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWallet } from '../components/WalletContext';
import { web } from '@klever/sdk-web';
import {
  FaWallet,
  FaImage,
  FaCoins,
  FaLayerGroup,
  FaCube,
  FaChevronRight,
  FaInbox,
  FaSpinner,
  FaPaperPlane,
  FaTimes,
  FaMinus,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import '../styles/WalletPage.css';

const WalletPage = () => {
  const { walletAddress, useKdaFee, selectedKda } = useWallet();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Send modal state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendingAsset, setSendingAsset] = useState(null);
  const [sendToAddress, setSendToAddress] = useState('');
  const [sendQuantity, setSendQuantity] = useState(1);
  const [sendStatus, setSendStatus] = useState(''); // 'processing', 'success', 'error'
  const [sendMessage, setSendMessage] = useState('');

  const isTokensPage = location.pathname.includes('/tokens');
  const isSFTsPage = location.pathname.includes('/sfts');

  const replaceLogoUri = (assetId, logoUrl) => {
    if (assetId === 'WCNFT-25HY') return null;
    if (!logoUrl) return logoUrl;
    if (logoUrl.includes("https://klever-mint.mypinata.cloud")) {
      return logoUrl.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
    }
    return logoUrl;
  };

  useEffect(() => {
    const fetchAssetLogo = async (assetId) => {
      try {
        const response = await fetch(`https://api.mainnet.klever.org/v1.0/assets/${assetId}`);
        const data = await response.json();
        return replaceLogoUri(assetId, data.data.asset.logo);
      } catch (error) {
        console.error('Error fetching asset logo:', error);
        return '';
      }
    };

    // For SFTs, fetch individual item metadata to get the actual image
    const fetchSFTMetadata = async (assetId) => {
      try {
        // assetId is in format "COLLECTION/NONCE" e.g. "PMC-38GL/14"
        const [collectionId, nonce] = assetId.split('/');
        if (!nonce) {
          // Not an individual SFT, fetch collection logo
          return await fetchAssetLogo(assetId);
        }

        // First get the collection to find metadata URI
        const collectionResponse = await fetch(`https://api.mainnet.klever.org/v1.0/assets/${collectionId}`);
        const collectionData = await collectionResponse.json();
        const collection = collectionData.data?.asset;

        if (!collection?.uris) {
          return await fetchAssetLogo(collectionId);
        }

        // Find metadata URI
        const metadataObj = collection.uris.find(uri => uri.key.toLowerCase() === 'metadata');
        if (!metadataObj?.value) {
          return await fetchAssetLogo(collectionId);
        }

        let metadataUri = metadataObj.value;

        // Handle different URI formats
        if (metadataUri.startsWith('ipfs://')) {
          metadataUri = metadataUri.replace('ipfs://', 'https://ipfs.io/ipfs/');
        }
        if (metadataUri.includes('klever-mint.mypinata.cloud')) {
          metadataUri = metadataUri.replace('https://klever-mint.mypinata.cloud', 'https://ipfs.io');
        }

        // Fetch the actual item metadata
        const itemMetadataUrl = `${metadataUri}/${nonce}`;
        const metadataResponse = await fetch(itemMetadataUrl);
        const metadata = await metadataResponse.json();

        let imageUrl = metadata.image;
        if (imageUrl) {
          if (imageUrl.startsWith('ipfs://')) {
            imageUrl = imageUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
          }
          if (imageUrl.includes('klever-mint.mypinata.cloud')) {
            imageUrl = imageUrl.replace('https://klever-mint.mypinata.cloud', 'https://ipfs.io');
          }
        }

        return imageUrl || await fetchAssetLogo(collectionId);
      } catch (error) {
        console.error('Error fetching SFT metadata:', error);
        // Fallback to collection logo
        const [collectionId] = assetId.split('/');
        return await fetchAssetLogo(collectionId);
      }
    };

    const fetchAssets = async () => {
      if (walletAddress) {
        setLoading(true);
        const url = `https://api.mainnet.klever.org/v1.0/address/${walletAddress}`;
        try {
          const response = await fetch(url);
          const data = await response.json();
          const assetType = isTokensPage ? 0 : (isSFTsPage ? 2 : 1);
          const assetEntries = Object.entries(data.data.account.assets || {});
          const filteredAssets = await Promise.all(
            assetEntries
              .filter(([, assetData]) => assetData.assetType === assetType)
              .map(async ([, assetData]) => {
                // For SFTs, fetch individual item metadata; for others, fetch collection logo
                const logoUrl = isSFTsPage
                  ? await fetchSFTMetadata(assetData.assetId)
                  : await fetchAssetLogo(assetData.assetId);

                return {
                  assetId: assetData.assetId,
                  name: assetData.assetName,
                  balance: assetType === 0
                    ? adjustBalance(assetData.balance, assetData.precision)
                    : assetData.balance,
                  assetType: assetData.assetType,
                  precision: assetData.precision || 0,
                  logoUrl,
                };
              })
          );
          setAssets(filteredAssets);
        } catch (error) {
          console.error('Error fetching assets:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAssets();
  }, [walletAddress, location.pathname, isTokensPage, isSFTsPage]);

  const adjustBalance = (balance, precision) => {
    const adjusted = balance / Math.pow(10, precision);
    return adjusted.toLocaleString('en-US', {
      minimumFractionDigits: Math.min(precision, 2),
      maximumFractionDigits: Math.min(precision, 8)
    });
  };

  const formatBalance = (balance) => {
    if (typeof balance === 'string') return balance;
    return balance.toLocaleString('en-US');
  };

  const handleViewClick = (assetId) => {
    if (isTokensPage) {
      navigate(`/wallet/tokens/${assetId}`);
    } else if (isSFTsPage) {
      // For SFTs, assetId is in format "COLLECTION/NONCE"
      // Navigate to individual item details page
      const [collectionId, nonce] = assetId.split('/');
      if (nonce) {
        // Individual SFT - navigate to NftDetailsPage
        navigate(`/collections/${collectionId}/${nonce}`);
      } else {
        // SFT Collection - navigate to AssetDetailsPage
        navigate(`/wallet/${assetId}?page=1`);
      }
    } else {
      navigate(`/wallet/${assetId}?page=1`);
    }
  };

  const handleTabChange = (path) => {
    navigate(path);
  };

  // SFT Send Modal Functions
  const openSendModal = (asset) => {
    setSendingAsset(asset);
    setSendToAddress('');
    setSendQuantity(1);
    setSendStatus('');
    setSendMessage('');
    setSendModalOpen(true);
  };

  const closeSendModal = () => {
    setSendModalOpen(false);
    setSendingAsset(null);
    setSendToAddress('');
    setSendQuantity(1);
    setSendStatus('');
    setSendMessage('');
  };

  const handleQuantityChange = (newQty) => {
    if (!sendingAsset) return;
    const maxQty = sendingAsset.balance;
    const qty = Math.max(1, Math.min(maxQty, parseInt(newQty) || 1));
    setSendQuantity(qty);
  };

  const handleSendSFT = async () => {
    if (!sendToAddress) {
      setSendStatus('error');
      setSendMessage('Please enter a recipient address.');
      return;
    }

    if (!sendToAddress.startsWith('klv1')) {
      setSendStatus('error');
      setSendMessage("Invalid address. Klever addresses start with 'klv1'.");
      return;
    }

    if (sendQuantity < 1 || sendQuantity > sendingAsset.balance) {
      setSendStatus('error');
      setSendMessage(`Quantity must be between 1 and ${sendingAsset.balance}`);
      return;
    }

    setSendStatus('processing');
    setSendMessage('Processing transfer...');

    try {
      const transferPayload = {
        receiver: sendToAddress,
        kda: sendingAsset.assetId,
        amount: sendQuantity,
      };

      let txOptions = {};
      if (useKdaFee && selectedKda) {
        txOptions = { kdaFee: selectedKda };
      }

      const transferTx = await web.buildTransaction(
        [{ payload: transferPayload, type: 0 }],
        undefined,
        txOptions
      );

      const signedTx = await web.signTransaction(transferTx);

      if (!signedTx) {
        setSendStatus('error');
        setSendMessage('Transaction was rejected');
        return;
      }

      await web.broadcastTransactions([signedTx]);

      setSendStatus('success');
      setSendMessage(`Successfully sent ${sendQuantity} ${sendingAsset.name}!`);

      // Update the asset balance in state
      setAssets(prev => prev.map(a => {
        if (a.assetId === sendingAsset.assetId) {
          const newBalance = a.balance - sendQuantity;
          return newBalance > 0 ? { ...a, balance: newBalance } : null;
        }
        return a;
      }).filter(Boolean));

      // Close modal after success
      setTimeout(closeSendModal, 2000);
    } catch (error) {
      console.error('Error sending SFT:', error);
      setSendStatus('error');
      setSendMessage(error.message || 'Transfer failed. Please try again.');
    }
  };

  return (
    <div className="wallet-page">
      {/* Hero Section */}
      <div className="wallet-hero">
        <div className="wallet-hero-content">
          <h1 className="wallet-hero-title">
            <FaWallet className="hero-icon" />
            {isTokensPage ? 'Your Tokens' : (isSFTsPage ? 'Your SFTs' : 'Your NFTs')}
          </h1>
          <p className="wallet-hero-subtitle">
            Manage your {isTokensPage ? 'token' : (isSFTsPage ? 'Semi-Fungible Token' : 'NFT')} collection on KleverChain
          </p>
        </div>

        {/* Stats */}
        <div className="wallet-stats-bar">
          <div className="wallet-stat-card">
            <div className="stat-icon-wrapper">
              <FaLayerGroup className="stat-icon" />
            </div>
            <div className="stat-content">
              <span className="stat-label">
                {isTokensPage ? 'Tokens' : (isSFTsPage ? 'SFT Collections' : 'Collections')}
              </span>
              <span className="stat-value">{assets.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="wallet-tab-navigation">
        <button
          className={`wallet-tab-btn ${!isTokensPage && !isSFTsPage ? 'active' : ''}`}
          onClick={() => handleTabChange('/wallet')}
        >
          <FaImage className="tab-icon" />
          <span>NFTs</span>
        </button>
        <button
          className={`wallet-tab-btn ${isSFTsPage ? 'active' : ''}`}
          onClick={() => handleTabChange('/wallet/sfts')}
        >
          <FaCube className="tab-icon" />
          <span>SFTs</span>
        </button>
        <button
          className={`wallet-tab-btn ${isTokensPage ? 'active' : ''}`}
          onClick={() => handleTabChange('/wallet/tokens')}
        >
          <FaCoins className="tab-icon" />
          <span>Tokens</span>
        </button>
      </div>

      {/* Assets Grid */}
      <div className="wallet-content">
        {loading ? (
          <div className="wallet-loading">
            <FaSpinner className="loading-spinner" />
            <p className="loading-text">Loading your {isTokensPage ? 'tokens' : (isSFTsPage ? 'SFTs' : 'NFTs')}...</p>
          </div>
        ) : assets.length === 0 ? (
          <div className="wallet-empty-state">
            <FaInbox className="empty-icon" />
            <h2>No {isTokensPage ? 'Tokens' : (isSFTsPage ? 'SFTs' : 'NFTs')} Found</h2>
            <p>Your wallet doesn't have any {isTokensPage ? 'tokens' : (isSFTsPage ? 'Semi-Fungible Tokens' : 'NFTs')} yet.</p>
          </div>
        ) : (
          <div className="wallet-assets-grid">
            {assets.map((asset, index) => (
              <div
                key={asset.assetId}
                className="wallet-asset-card"
                style={{ animationDelay: `${Math.min(index * 0.03, 1)}s` }}
              >
                <div className="asset-card-header">
                  {asset.logoUrl ? (
                    <img
                      src={asset.logoUrl}
                      alt={asset.name}
                      className="asset-logo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <div className="asset-logo-placeholder">
                      {isTokensPage ? <FaCoins /> : <FaImage />}
                    </div>
                  )}
                  {!asset.logoUrl && (
                    <div className="asset-logo-placeholder" style={{ display: 'none' }}>
                      {isTokensPage ? <FaCoins /> : <FaImage />}
                    </div>
                  )}
                </div>

                <div className="asset-card-content">
                  <h3 className="asset-name">{asset.name}</h3>
                  <p className="asset-id">{asset.assetId}</p>

                  <div className="asset-balance-section">
                    <span className="balance-label">
                      {isTokensPage ? 'Balance' : (isSFTsPage ? 'Balance' : 'Owned')}
                    </span>
                    <span className="balance-value">
                      {formatBalance(asset.balance)}
                      {isSFTsPage && <span className="sft-units-label"> units</span>}
                    </span>
                  </div>

                  <div className="asset-action-buttons">
                    <button
                      className="asset-action-btn"
                      onClick={() => handleViewClick(asset.assetId)}
                    >
                      <span>{isTokensPage ? 'Details' : (isSFTsPage ? 'View Details' : 'View Collection')}</span>
                      <FaChevronRight className="action-arrow" />
                    </button>
                    {isSFTsPage && (
                      <button
                        className="asset-send-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openSendModal(asset);
                        }}
                        title="Send SFT"
                      >
                        <FaPaperPlane />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SFT Send Modal */}
      {sendModalOpen && sendingAsset && (
        <div className="sft-send-modal-overlay" onClick={closeSendModal}>
          <div className="sft-send-modal" onClick={(e) => e.stopPropagation()}>
            <div className="send-modal-header">
              <h3><FaPaperPlane /> Send SFT</h3>
              <button className="modal-close-btn" onClick={closeSendModal}>
                <FaTimes />
              </button>
            </div>

            <div className="send-modal-content">
              {/* Asset Preview */}
              <div className="send-asset-preview">
                {sendingAsset.logoUrl ? (
                  <img src={sendingAsset.logoUrl} alt={sendingAsset.name} />
                ) : (
                  <div className="preview-placeholder"><FaCube /></div>
                )}
                <div className="send-asset-info">
                  <span className="send-asset-name">{sendingAsset.name}</span>
                  <span className="send-asset-id">{sendingAsset.assetId}</span>
                  <span className="send-asset-balance">Balance: {sendingAsset.balance} units</span>
                </div>
              </div>

              {/* Recipient Address */}
              <div className="send-form-group">
                <label>Recipient Address</label>
                <input
                  type="text"
                  placeholder="klv1..."
                  value={sendToAddress}
                  onChange={(e) => setSendToAddress(e.target.value)}
                  disabled={sendStatus === 'processing'}
                />
              </div>

              {/* Quantity */}
              <div className="send-form-group">
                <label>Quantity</label>
                <div className="quantity-controls">
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(sendQuantity - 1)}
                    disabled={sendQuantity <= 1 || sendStatus === 'processing'}
                  >
                    <FaMinus />
                  </button>
                  <input
                    type="number"
                    value={sendQuantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    min="1"
                    max={sendingAsset.balance}
                    disabled={sendStatus === 'processing'}
                  />
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(sendQuantity + 1)}
                    disabled={sendQuantity >= sendingAsset.balance || sendStatus === 'processing'}
                  >
                    <FaPlus />
                  </button>
                  <span className="max-qty">/ {sendingAsset.balance}</span>
                </div>
              </div>

              {/* Status Message */}
              {sendMessage && (
                <div className={`send-status-message ${sendStatus}`}>
                  {sendStatus === 'processing' && <FaSpinner className="spin" />}
                  {sendStatus === 'success' && <FaCheckCircle />}
                  {sendStatus === 'error' && <FaTimesCircle />}
                  <span>{sendMessage}</span>
                </div>
              )}
            </div>

            <div className="send-modal-actions">
              <button
                className="btn-cancel"
                onClick={closeSendModal}
                disabled={sendStatus === 'processing'}
              >
                Cancel
              </button>
              <button
                className="btn-send"
                onClick={handleSendSFT}
                disabled={sendStatus === 'processing' || sendStatus === 'success'}
              >
                {sendStatus === 'processing' ? (
                  <><FaSpinner className="spin" /> Sending...</>
                ) : (
                  <><FaPaperPlane /> Send</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WalletPage;